// @flow
import * as React from 'react';

import {
  selectIsAuthenticatedUser,
  selectTaxesDisplaySetting,
} from '@gandi/authentication';
import { selectPricingInfos } from '@gandi/billing';
import type { BundleType } from '@gandi/cart';
import { selectHasVat } from '@gandi/redux-places';
import {
  sendSelfPromotion,
  setProps,
  type OnsiteAdsProps,
} from '@gandi/tracking';
import { localeSelector as selectLocale } from '@gandi/translate';

import { useAppSelector } from 'src/hooks/useAppSelector';

import { selectCurrentBU } from '../modules/organization/selectors';

import type { InstanceSize } from '../modules/simplehosting/prices';
import type { FullState } from '../types/index';

export const useTrackingProps = () => {
  const { grid, country, currency } = useAppSelector(selectPricingInfos);
  const authenticated = useAppSelector(selectIsAuthenticatedUser);
  const locale = useAppSelector(selectLocale);

  const taxesIncluded = useAppSelector(selectTaxesDisplaySetting);
  const hasTaxes = useAppSelector((state: FullState) =>
    selectHasVat(state, country),
  );
  const taxesDisplay = taxesIncluded ? 'true' : 'false';
  const currentBU = useAppSelector(selectCurrentBU);

  React.useEffect(() => {
    setProps(
      {
        country,
        currency,
        grid,
        lang: locale,
        loggedin: authenticated,
        taxes: hasTaxes || authenticated ? taxesDisplay : 'N/A',
      },
      {
        persistent: true,
        events: ['page.*'],
      },
    );
  }, [country, currency, grid, locale, authenticated, hasTaxes, taxesDisplay]);

  React.useEffect(
    () =>
      setProps(
        {
          bu: currentBU,
        },
        {
          persistent: true,
          events: ['*'],
        },
      ),
    [currentBU],
  );
};

export type SelfPromotionParams = {
  /** Main Campaign type (name of the Offer) */
  campaign: 'bundle' | 'xsell_hosting' | 'cart_suggest',
  /** Product version (bundle type, hosting size, product type, ...) */
  category: BundleType | InstanceSize | 'domain' | void,
  /** Included TLD, or underscore-separated TLDs:
   * @example 'fr' or 'com_net_org'
   */
  variant: string | void,
  /** Page or panel where it's displayed. */
  general_placement:
    | 'shop_suggest'
    | 'cart_overview'
    | 'cart_widget'
    | 'payment_return',
  /** More specific placement in the page. */
  detailed_placement?: 'provided_row' | 'suggested_row' | 'bottom',
  ...
};
export type SelfPromotionProps =
  | void
  | false
  | (SelfPromotionParams & OnsiteAdsProps);

/** Pass `undefined` until you are ready to track, without missing data.\
 * The props object must be memoized.\
 * Once you pass an object, it will send the `impression` event.\
 * Use `sendSelfPromotionClick` to send the `click` event when the user adds the offer to the cart.
 */
export function useSelfPromotionTracking(memoizedProps: SelfPromotionProps): {
  sendSelfPromotionClick: () => void,
} {
  // Track `impression` on offer's render
  React.useEffect(() => {
    if (memoizedProps)
      sendSelfPromotion({
        type: 'self_promotion',
        impression: memoizedProps,
      });
  }, [memoizedProps]);

  const sendSelfPromotionClick = React.useCallback(() => {
    // Track `click` when adding offer to cart
    if (memoizedProps)
      sendSelfPromotion({
        type: 'self_promotion',
        click: memoizedProps,
      });
  }, [memoizedProps]);

  return { sendSelfPromotionClick };
}
